import { useState, type FormEvent } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import type { ShiftOutcomeKind } from '../index.js'
import type { WorkerTableRow } from '../../roster/index.js'
import { ADMIN_API_BASE_URL, EndpointHint, Skeleton } from '../../core/index.js'
import { getWorkerScore, listWorkerOutcomes, sendCallout, sendNoShow, sendShiftComplete, type EventAccepted, type PartnerSession } from '../lib/partnerApi.js'
import { OutcomeLog } from '../../scoring/index.js'
import { Send } from 'lucide-react'
import { ErrorNote, INPUT_CLASS, Labeled, SuccessNote, describeError } from './formControls.js'
import { Button } from '../../core/index.js'
import { LoadFailure } from '../../core/index.js'

const DEFAULT_SHIFT_START_HOUR = 7
const DEFAULT_SHIFT_END_HOUR = 15

/** Today at the given hour, in the `YYYY-MM-DDTHH:mm` form a datetime-local input uses. */
function todayAt(hour: number): string {
  const now = new Date()
  const pad = (value: number): string => String(value).padStart(2, '0')
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(hour)}:00`
}

function toIso(localDateTime: string): string {
  return new Date(localDateTime).toISOString()
}

type TimeField = 'scheduledStart' | 'scheduledEnd' | 'actualStart' | 'actualEnd' | 'reportedAt'

const TIME_LABELS: Record<TimeField, string> = {
  scheduledStart: 'Scheduled start',
  scheduledEnd: 'Scheduled end',
  actualStart: 'Actual start',
  actualEnd: 'Actual end',
  reportedAt: 'Notice given at',
}

interface KindPresentation {
  path: string
  button: string
  /** The times this kind of event carries, in the order the form shows them. */
  timeFields: TimeField[]
}

const KINDS: Record<ShiftOutcomeKind, KindPresentation> = {
  completed: { path: '/events/shift-complete', button: 'Send shift-complete', timeFields: ['scheduledStart', 'actualStart', 'scheduledEnd', 'actualEnd'] },
  callout: { path: '/events/callout', button: 'Send call-out', timeFields: ['scheduledStart', 'scheduledEnd', 'reportedAt'] },
  no_show: { path: '/events/no-show', button: 'Send no-show', timeFields: ['scheduledStart', 'scheduledEnd'] },
}

interface ShiftOutcomeFormProps {
  kind: ShiftOutcomeKind
  session: PartnerSession
  facilityId: string
  workers: WorkerTableRow[]
}

/**
 * Sends one of the three shift-outcome events as the partner, then shows what
 * the API now has on record for that worker - so the effect of the call is
 * visible next to the call.
 */
/** What the form says about the shift's chart: nothing, done or not done. */
type ChartChoice = 'unreported' | 'completed' | 'missed'

export function ShiftOutcomeForm({ kind, session, facilityId, workers }: ShiftOutcomeFormProps) {
  const queryClient = useQueryClient()
  const presentation = KINDS[kind]
  const [workerId, setWorkerId] = useState('')
  const [unitId, setUnitId] = useState('')
  const [idempotencyKey, setIdempotencyKey] = useState('')
  const [times, setTimes] = useState<Record<TimeField, string>>({
    scheduledStart: todayAt(DEFAULT_SHIFT_START_HOUR),
    scheduledEnd: todayAt(DEFAULT_SHIFT_END_HOUR),
    actualStart: todayAt(DEFAULT_SHIFT_START_HOUR),
    actualEnd: todayAt(DEFAULT_SHIFT_END_HOUR),
    reportedAt: todayAt(DEFAULT_SHIFT_START_HOUR - 2),
  })
  const [isBridge, setIsBridge] = useState(false)
  const [chart, setChart] = useState<ChartChoice>('unreported')
  const [isSending, setIsSending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [accepted, setAccepted] = useState<EventAccepted | null>(null)
  /** The score as it stood before the last event was sent, so the effect of the call shows as before and after. */
  const [scoreBefore, setScoreBefore] = useState<number | null>(null)

  const outcomesKey = ['partner-simulator', 'outcomes', session.clientId, workerId]
  const outcomesQuery = useQuery({
    queryKey: outcomesKey,
    queryFn: () => listWorkerOutcomes(ADMIN_API_BASE_URL ?? '', session, workerId),
    enabled: workerId !== '' && Boolean(ADMIN_API_BASE_URL),
  })
  const scoreKey = ['partner-simulator', 'score', session.clientId, workerId]
  const scoreQuery = useQuery({
    queryKey: scoreKey,
    queryFn: () => getWorkerScore(ADMIN_API_BASE_URL ?? '', session, workerId),
    enabled: workerId !== '' && Boolean(ADMIN_API_BASE_URL),
  })

  const hasAllTimes = presentation.timeFields.every((field) => times[field] !== '')
  const canSend = workerId !== '' && unitId.trim() !== '' && hasAllTimes && !isSending

  function send(baseUrl: string): Promise<EventAccepted> {
    const base = {
      facility_id: facilityId,
      worker_id: workerId,
      shift_date: times.scheduledStart.slice(0, 10),
      scheduled_start: toIso(times.scheduledStart),
      scheduled_end: toIso(times.scheduledEnd),
      unit_id: unitId.trim(),
    }
    const key = idempotencyKey.trim()
    if (kind === 'completed') {
      return sendShiftComplete(baseUrl, session, { ...base, actual_start: toIso(times.actualStart), actual_end: toIso(times.actualEnd), ...(isBridge ? { clinical_bridge: true } : {}), ...(chart === 'unreported' ? {} : { chart_completed: chart === 'completed' }) }, key)
    }
    if (kind === 'callout') {
      return sendCallout(baseUrl, session, { ...base, reported_at: toIso(times.reportedAt) }, key)
    }
    return sendNoShow(baseUrl, session, base, key)
  }

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault()
    if (!canSend || !ADMIN_API_BASE_URL) {
      return
    }
    setIsSending(true)
    setError(null)
    setAccepted(null)
    setScoreBefore(scoreQuery.data?.score ?? null)
    try {
      setAccepted(await send(ADMIN_API_BASE_URL))
      await Promise.all([queryClient.invalidateQueries({ queryKey: outcomesKey }), queryClient.invalidateQueries({ queryKey: scoreKey })])
    } catch (err) {
      setError(describeError(err, 'The event could not be sent.'))
    } finally {
      setIsSending(false)
    }
  }

  return (
    <form onSubmit={(event) => void handleSubmit(event)} className="space-y-4">
      <div className="grid gap-5 sm:grid-cols-2">
        <Labeled label="Worker" htmlFor="outcome-worker" hint={workers.length === 0 ? 'This facility has no workers on its roster yet.' : undefined}>
          <select
            id="outcome-worker"
            className={INPUT_CLASS}
            value={workerId}
            onChange={(event) => {
              setWorkerId(event.target.value)
              setAccepted(null)
            }}
          >
            <option value="">Choose a worker…</option>
            {workers.map((worker) => (
              <option key={worker.workerId} value={worker.workerId}>
                {worker.name} ({worker.workerId})
              </option>
            ))}
          </select>
        </Labeled>
        <Labeled label="Unit" htmlFor="outcome-unit" hint="The partner's identifier for the unit the shift was on.">
          <input id="outcome-unit" className={INPUT_CLASS} value={unitId} onChange={(event) => setUnitId(event.target.value)} />
        </Labeled>
        {presentation.timeFields.map((field) => (
          <Labeled key={field} label={TIME_LABELS[field]} htmlFor={`outcome-${field}`}>
            <input
              id={`outcome-${field}`}
              type="datetime-local"
              className={INPUT_CLASS}
              value={times[field]}
              onChange={(event) => setTimes({ ...times, [field]: event.target.value })}
            />
          </Labeled>
        ))}
        {kind === 'completed' && (
          <label className="flex items-center gap-2 text-sm text-ink cursor-pointer sm:col-span-2">
            <input type="checkbox" checked={isBridge} onChange={(event) => setIsBridge(event.target.checked)} />
            <span>
              Clinical Bridge shift <span className="text-[11px] text-ink-mid">· sends <span className="font-mono">clinical_bridge: true</span>; the scoring engine adds the facility’s boost per Bridge shift worked</span>
            </span>
          </label>
        )}
        {kind === 'completed' && (
          <Labeled label="Chart" htmlFor="outcome-chart" hint="One shift, one chart. Sends chart_completed, which feeds Charting Completion; not reported leaves the shift out of it.">
            <select id="outcome-chart" className={INPUT_CLASS} value={chart} onChange={(event) => setChart(event.target.value as ChartChoice)}>
              <option value="unreported">Not reported</option>
              <option value="completed">Completed</option>
              <option value="missed">Not completed</option>
            </select>
          </Labeled>
        )}
        <Labeled
          label="Idempotency key"
          htmlFor="outcome-idempotency-key"
          hint="Optional. Send the same key twice to see a retry answered with the original event instead of a second record."
        >
          <input
            id="outcome-idempotency-key"
            className={`${INPUT_CLASS} font-mono`}
            value={idempotencyKey}
            onChange={(event) => setIdempotencyKey(event.target.value)}
            autoComplete="off"
          />
        </Labeled>
      </div>

      <Button icon={Send} size="lg" type="submit" disabled={!canSend}>
        {isSending ? 'Sending…' : presentation.button}
      </Button>
      <EndpointHint className="ml-3">POST {presentation.path}</EndpointHint>

      {error && <ErrorNote message={error} />}
      {accepted && (
        <SuccessNote>
          {accepted.duplicate ? (
            <>
              Already on record as <span className="font-mono">{accepted.eventId}</span>. The API recognised the idempotency key and stored nothing new.
            </>
          ) : (
            <>
              Stored as <span className="font-mono">{accepted.eventId}</span>. It is on the worker's record below, and the running score was recalculated with it.
            </>
          )}
        </SuccessNote>
      )}

      {workerId !== '' && (
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 text-[11px]">
          <span className="text-[10px] font-bold uppercase tracking-wide text-ink-mid">
            Running score <EndpointHint>GET /workers/{workerId}/score</EndpointHint>
          </span>
          {scoreQuery.isPending ? (
            <span className="text-ink-mid">Loading…</span>
          ) : scoreQuery.isError ? (
            <LoadFailure size="xs" message={describeError(scoreQuery.error, 'Could not load this worker’s score.')} onRetry={() => void scoreQuery.refetch()} retrying={scoreQuery.isFetching} />
          ) : (
            <span className="font-bold text-ink">
              {accepted && !accepted.duplicate && scoreBefore !== null && <span className="font-normal text-ink-mid">{scoreBefore.toFixed(1)} → </span>}
              {scoreQuery.data.score === null ? 'not enough activity to score' : scoreQuery.data.score.toFixed(1)}
              {scoreQuery.data.provisional && <span className="ml-1 font-normal text-ink-mid">(provisional)</span>}
              <span className="ml-2 font-normal text-ink-mid">
                {scoreQuery.data.scheduledShifts} scheduled {scoreQuery.data.scheduledShifts === 1 ? 'shift' : 'shifts'} this period
              </span>
            </span>
          )}
        </div>
      )}

      {workerId !== '' && (
        <div>
          <div className="text-[10px] font-bold uppercase tracking-wide text-ink-mid mb-1.5">
            On record for this worker <EndpointHint>GET /workers/{workerId}/outcomes</EndpointHint>
          </div>
          {outcomesQuery.isPending ? (
            <Skeleton label="Loading outcomes" />
          ) : outcomesQuery.isError ? (
            <LoadFailure size="xs" message={describeError(outcomesQuery.error, 'Could not load this worker’s outcomes.')} onRetry={() => void outcomesQuery.refetch()} retrying={outcomesQuery.isFetching} />
          ) : (
            <OutcomeLog outcomes={outcomesQuery.data} />
          )}
        </div>
      )}
    </form>
  )
}
